import {useContext, useState} from "react";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import {Button} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import axiApi from "ApiCaller";
import useCustomTranslation from "core/customTranslation";
import {SwalAlertClass} from "core/swalClass";
import AuthContext from "context/AuthContext";

const CSV_MAX_DAYS = 7;

const translations = {
    es: {
        "common.accept": "Aceptar",
        "export.title": "Exportar",
        "export.success": "La tarea se ha lanzado. Recibirás una notificación cuando finalice.",
        "export.error": "No se ha podido procesar la solicitud.",
        "csv.title": "Descargar CSV",
        "csv.text": "Exporta los logs con los filtros aplicados. Máximo 7 días para no saturar la base de datos.",
        "csv.limit": "El rango seleccionado es de {{days}} días. Reduce el rango a 7 días o menos.",
        "csv.button": "Descargar CSV",
        "alerts.title": "Informe de alertas",
        "alerts.text": "Excel con el número de alertas por dispositivo y un resumen por tramos.",
        "alerts.button": "Descargar informe",
        "alerts.all_panels": "Todos los paneles",
    },
    en: {
        "common.accept": "Accept",
        "export.title": "Export",
        "export.success": "The task has been launched. You will receive a notification when it finishes.",
        "export.error": "The request could not be processed.",
        "csv.title": "Download CSV",
        "csv.text": "Exports the logs with the applied filters. Up to 7 days so the database is not overloaded.",
        "csv.limit": "The selected range is {{days}} days. Reduce it to 7 days or less.",
        "csv.button": "Download CSV",
        "alerts.title": "Alerts report",
        "alerts.text": "Excel with the number of alerts per device and a summary by ranges.",
        "alerts.button": "Download report",
        "alerts.all_panels": "All panels",
    },
};

// Días del rango, incluidos el primero y el último
const spanDays = (start, end) => {
    if (!start || !end) return 0;
    const startMs = new Date(`${start}T00:00:00`).getTime();
    const endMs = new Date(`${end}T00:00:00`).getTime();
    if (Number.isNaN(startMs) || Number.isNaN(endMs)) return 0;
    return Math.round((endMs - startMs) / (1000 * 60 * 60 * 24)) + 1;
};

// YYYY-MM-DD → DD-MM-YYYY
const displayDate = (date) => (date ? date.split("-").reverse().join("-") : "");

const ModalExport = ({show, setShow, filtersRef}) => {
    const {t: _TC} = useCustomTranslation(translations);
    const Alert = new SwalAlertClass({confirmButtonText: _TC("common.accept")});
    const [sending, setSending] = useState("");
    const {user} = useContext(AuthContext);
    const [allPanels, setAllPanels] = useState(false);

    const {start_date, end_date} = filtersRef.current.chosen;
    const days = spanDays(start_date, end_date);
    const csvAllowed = days > 0 && days <= CSV_MAX_DAYS;

    const handleClose = () => {
        setAllPanels(false);
        setShow(false);
    };

    const request = (key, url, data) => {
        setSending(key);
        axiApi
            .post(url, data)
            .then((resp) => {
                if (resp.data && resp.data.status) {
                    handleClose();
                    Alert.success(_TC(`${key}.title`), _TC("export.success"));
                } else {
                    Alert.error(_TC(`${key}.title`), resp.data?.message || _TC("export.error"));
                }
            })
            .catch((error) => {
                Alert.error(_TC(`${key}.title`), error.response?.data?.message || _TC("export.error"));
            })
            .finally(() => setSending(""));
    };

    const onClickCsv = () => request("csv", "/api/loggers/messages/export/", {...filtersRef.current.chosen});
    const onClickAlerts = () =>
        request("alerts", "/api/loggers/messages/alerts/export/", {start_date, end_date, all_panels: allPanels});

    return (
        <Modal show={show} onHide={handleClose} size="lg" centered>
            <Modal.Header closeButton className="preview-canvas mb-0">
                <div className="preview-icon">
                    <FontAwesomeIcon icon="file-export" />
                </div>
                <div className="preview-title d-inline-flex align-items-center">
                    <Modal.Title className="d-inline-flex ms-1">
                        <div>
                            <div className="h4 ms-0">{_TC("export.title")}</div>
                            <div className="h5 ms-0">
                                <small>
                                    {displayDate(start_date)} → {displayDate(end_date)}
                                </small>
                            </div>
                        </div>
                    </Modal.Title>
                </div>
            </Modal.Header>
            <Modal.Body>
                <div className="d-flex align-items-center border-bottom pb-3 mb-3">
                    <div className="flex-grow-1 me-3">
                        <h5>{_TC("csv.title")}</h5>
                        <div className="text-secondary">{_TC("csv.text")}</div>
                        {!csvAllowed && (
                            <div className="text-danger small mt-1">
                                {_TC("csv.limit").replace("{{days}}", days)}
                            </div>
                        )}
                    </div>
                    <Button variant="outline-primary" disabled={!csvAllowed || !!sending} onClick={onClickCsv}>
                        <FontAwesomeIcon
                            icon={sending === "csv" ? "circle-notch" : "file-export"}
                            spin={sending === "csv"}
                            className="me-2"
                        />
                        {_TC("csv.button")}
                    </Button>
                </div>
                <div className="d-flex align-items-center">
                    <div className="flex-grow-1 me-3">
                        <h5>{_TC("alerts.title")}</h5>
                        <div className="text-secondary">{_TC("alerts.text")}</div>
                        {user?.is_superuser && (
                            <Form.Check
                                type="checkbox"
                                id="alerts-all-panels"
                                className="mt-2"
                                label={_TC("alerts.all_panels")}
                                checked={allPanels}
                                onChange={(e) => setAllPanels(e.target.checked)}
                            />
                        )}
                    </div>
                    <Button variant="outline-primary" disabled={!!sending} onClick={onClickAlerts}>
                        <FontAwesomeIcon
                            icon={sending === "alerts" ? "circle-notch" : "file-excel"}
                            spin={sending === "alerts"}
                            className="me-2"
                        />
                        {_TC("alerts.button")}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default ModalExport;
